import React, { useEffect, useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "../.././assets/imgs/arrow-back-left.svg";
import { VerififcationOtp } from "../VerififcationOtp";
import VerifyPhoneNumberWidget from "./VerifyPhoneNumberWidget";
import { useSupabaseAuth } from "../../Context/AuthContext/AuthContext";
import { hapticsImpactLight } from "../../lib/utils";
import { IonHeader, IonToolbar } from "@ionic/react";
import { useMixpanel } from "../../Context/MixpanelContext/MixpanelProvider";
import { AnimatePresence, motion } from "framer-motion";
import { backwardVariants, forwardVariants } from "../../Routes/AnimatedPage";

const STEP = ["INPUT_PHONE", "ENTER_CODE"];

export const VerificationPhoneNumber: React.FC = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [isValid, setIsValid] = useState(true);
  const [step, setStep] = useState<string>(STEP[0]);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const { sendSMS, verifyOTPCode, resendOtpCode } = useSupabaseAuth();
  const [errors, setErrors] = useState<string | null>(null);
  const [errorsPhoneNumber, setErrorsPhoneNumber] = useState<string | null>(
    null,
  );
  const [token, setToken] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const numInputs = 6;
  const Mixpanel = useMixpanel();
  const handlePhoneNumberChange = (value: string) => {
    if (value !== null) {
      setPhoneNumber(value);
      setIsValid(isValidPhoneNumber(value));
      setErrorsPhoneNumber(null);
    }
  };

  const handleSendSMS = async () => {
    const status = await sendSMS(phoneNumber);

    if (status) {
      console.log("Your SMS code has been sent");
      setDirection("forward");
      setStep(STEP[1]);
      setErrors(null);
    } else {
      setErrorsPhoneNumber("Sorry, failed to send SMS. Please try again.");
    }
  };

  const isValidToken = (token: string) => {
    if (!token) return false;
    if (token.length < numInputs) return false;
    return true;
  };

  const handleVerifyOtp = async (phone: string, token: string) => {
    if (!isValidToken(token)) {
      setErrors("Please input verification code.");
      return;
    }

    const status = await verifyOTPCode(phoneNumber, token);
    if (status) {
      setErrors(null);
      navigate("/user-details");
    } else {
      setErrors("Incorrect verification code, please try again.");
    }
  };

  const handleResendCode = async () => {
    hapticsImpactLight();
    const status = await resendOtpCode(phoneNumber);

    if (status) {
      console.log("Your SMS code has been resent");
      setErrors(null);
      setSuccessMessage("OTP sent successfully");
      // Clear success message after 2 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 2000);
    } else {
      setErrors("Sorry your SMS could not be resent.");
    }
  };

  const handleGoBack = () => {
    hapticsImpactLight();
    switch (step) {
      case STEP[0]:
        navigate("/welcome", {
          state: {
            from: "verification-phone-number",
          },
        });
        break;
      default:
        setDirection("backward");
        setStep(STEP[0]);
        break;
    }
  };

  const onclickDone = () => {
    if (step === STEP[0] && isValid) {
      handleSendSMS();
    } else if (step === STEP[1]) {
      handleVerifyOtp(phoneNumber, token);
    }
  };

  const getDoneButtonStyles = () => {
    const baseStyles =
      "flex items-center justify-center rounded-[29px] cursor-pointer w-[90px] h-[35px]";
    const activeStyles = "bg-[#141926] text-white";
    const disabledStyles = "bg-[#141926] text-white/50 cursor-not-allowed";

    if (step === STEP[0]) {
      // For phone number input step
      return `${baseStyles} ${phoneNumber && isValid ? activeStyles : disabledStyles}`;
    } else {
      // For OTP input step
      return `${baseStyles} ${token && token.length === numInputs ? activeStyles : disabledStyles}`;
    }
  };

  const isButtonDisabled = () => {
    if (step === STEP[0]) {
      return !phoneNumber || !isValid;
    } else {
      return !token || token.length !== numInputs;
    }
  };

  useEffect(() => {
    if (step === STEP[0]) {
      Mixpanel.trackEvent("enter_phone_number_screen_open", {
        name: "Enter Phone Number Screen Open",
      });
    } else if (step === STEP[1]) {
      Mixpanel.trackEvent("enter_verification_code_screen_open", {
        name: "Enter Verification Code Screen Open",
      });
    }
  }, [step]);

  return (
    <div style={{ backgroundColor: "#000513" }}>
      <>
        <IonHeader className="ion-no-border modal-ion-header">
          <IonToolbar className="custom-toolbar">
            <div className="p-3 flex justify-between items-center sticky top-0 z-50">
              <div
                className="cursor-pointer rounded-[100px] w-[40px] h-[40px] flex justify-center items-center grayBtn"
                onClick={handleGoBack}
              >
                <img src={ArrowBackIcon} alt="" />
              </div>
              <div
                onClick={isButtonDisabled() ? undefined : onclickDone}
                className={getDoneButtonStyles()}
              >
                <span className="text-[16px] font-medium">Done</span>
              </div>
            </div>
          </IonToolbar>
        </IonHeader>

        <div className="flex flex-col h-full relative bottom-4">
          <AnimatePresence mode="wait">
            {step === STEP[0] ? (
              <motion.div
                key="phone-input"
                initial="initial"
                animate="in"
                exit="exit"
                variants={
                  direction === "forward" ? forwardVariants : backwardVariants
                }
              >
                <VerifyPhoneNumberWidget
                  phoneNumber={phoneNumber}
                  handlePhoneNumberChange={handlePhoneNumberChange}
                  isValid={isValid}
                  handleSendSMS={handleSendSMS}
                  errorsPhoneNumber={errorsPhoneNumber}
                  setErrorsPhoneNumber={setErrorsPhoneNumber}
                />
              </motion.div>
            ) : (
              <motion.div
                key="otp-input"
                initial="initial"
                animate="in"
                exit="exit"
                variants={
                  direction === "forward" ? forwardVariants : backwardVariants
                }
              >
                <VerififcationOtp
                  phone={phoneNumber}
                  onResendCode={handleResendCode}
                  localError={errors}
                  setLocalError={setErrors}
                  token={token}
                  setToken={setToken}
                  successMessage={successMessage}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </>
    </div>
  );
};
